// src/pages/ccp/PrepCcpVerifyPage.js
// Without :runId — QA queue of COMPLETED prep runs awaiting verification.
// With :runId — review a single run and verify & lock it, which auto-credits its real output
// into Ingredient Inventory. See docs/PREP_CCP_MODULE.md.
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import prepCcpService from '../../services/prepCcpService';
import sqfService from '../../services/sqfService';
import VisualInspectionsList from '../../components/VisualInspectionsList';
import { useLanguage, t } from '../../context/LanguageContext';

function QueueList() {
  const { lang } = useLanguage();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('COMPLETED');

  useEffect(() => {
    setLoading(true);
    prepCcpService.getRuns(tab).then(setRuns).catch(() => setRuns([])).finally(() => setLoading(false));
  }, [tab]);

  return (
    <div className="container py-4" style={{ maxWidth: 720 }}>
      <h4 className="fw-bold mb-3">🔍 {t('Prep CCP — QA Queue', 'Prep CCP — Cola de QA', lang)}</h4>
      <div className="btn-group mb-3">
        <button className={`btn btn-outline-secondary btn-sm ${tab === 'COMPLETED' ? 'active' : ''}`} onClick={() => setTab('COMPLETED')}>{t('Pending', 'Pendiente', lang)}</button>
        <button className={`btn btn-outline-secondary btn-sm ${tab === 'VERIFIED' ? 'active' : ''}`} onClick={() => setTab('VERIFIED')}>{t('Verified', 'Verificado', lang)}</button>
      </div>
      {loading ? (
        <div className="text-center py-4"><span className="spinner-border" /></div>
      ) : runs.length === 0 ? (
        <div className="text-muted">{t('Nothing here.', 'Nada aquí.', lang)}</div>
      ) : (
        <div className="list-group">
          {runs.map(r => (
            <Link key={r.id} to={`/ccp/prep/verify/${r.id}`} className="list-group-item list-group-item-action d-flex justify-content-between">
              <span>{r.Product?.name} — {r.Batch?.lotCode}</span>
              <span className="text-muted small">{r.completedAt ? new Date(r.completedAt).toLocaleDateString() : ''}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function minutesBetween(a, b) {
  if (!a || !b) return null;
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000);
}

// Compares the run's actual recorded times/temps against its product's configured critical
// limits and returns a list of plain-language deviation descriptions — empty if everything was
// met. This is what lets QA see at a glance whether corrective action needs to be documented
// before verifying, instead of having to manually re-check every number against the config.
function detectDeviations(run, config, lang) {
  if (!run || !config) return [];
  const issues = [];

  const cookReadings = (run.TempReadings || []).filter(r => r.stage === 'cook');
  const firstMet = cookReadings.find(r => Number(r.tempF) >= Number(config.cookMinTempF));
  if (run.cookFinalTempF != null && Number(run.cookFinalTempF) < Number(config.cookMinTempF)) {
    issues.push(t(
      `Cook: final temp ${Number(run.cookFinalTempF).toFixed(1)}°F did not reach the ${config.cookMinTempF}°F minimum.`,
      `Cocción: la temp. final ${Number(run.cookFinalTempF).toFixed(1)}°F no alcanzó el mínimo de ${config.cookMinTempF}°F.`,
      lang
    ));
  } else if (!firstMet && Number(config.cookHoldMinutes) > 0) {
    issues.push(t(
      `Cook: no temperature reading ever reached the ${config.cookMinTempF}°F minimum.`,
      `Cocción: ninguna lectura de temperatura alcanzó el mínimo de ${config.cookMinTempF}°F.`,
      lang
    ));
  } else if (firstMet && Number(config.cookHoldMinutes) > 0) {
    const heldMinutes = minutesBetween(firstMet.recordedAt, run.cookStoppedAt);
    if (heldMinutes != null && heldMinutes < Number(config.cookHoldMinutes)) {
      issues.push(t(
        `Cook: held at/above ${config.cookMinTempF}°F for only ${heldMinutes} min (required ${config.cookHoldMinutes} min).`,
        `Cocción: se mantuvo a/sobre ${config.cookMinTempF}°F solo ${heldMinutes} min (requerido ${config.cookHoldMinutes} min).`,
        lang
      ));
    }
  }

  const stage1Minutes = minutesBetween(run.coolStage1StartAt, run.coolStage1EndAt);
  if (stage1Minutes != null && stage1Minutes > Number(config.coolStage1MaxMinutes)) {
    issues.push(t(
      `Cooling Stage 1: took ${stage1Minutes} min, exceeding the ${config.coolStage1MaxMinutes} min limit.`,
      `Enfriamiento Etapa 1: tomó ${stage1Minutes} min, superando el límite de ${config.coolStage1MaxMinutes} min.`,
      lang
    ));
  }
  if (run.coolStage1EndTempF != null && Number(run.coolStage1EndTempF) > Number(config.coolStage1TargetTempF)) {
    issues.push(t(
      `Cooling Stage 1: ended at ${Number(run.coolStage1EndTempF).toFixed(1)}°F, above the ${config.coolStage1TargetTempF}°F target.`,
      `Enfriamiento Etapa 1: terminó a ${Number(run.coolStage1EndTempF).toFixed(1)}°F, sobre el objetivo de ${config.coolStage1TargetTempF}°F.`,
      lang
    ));
  }

  const stage2Minutes = minutesBetween(run.coolStage2StartAt, run.coolStage2EndAt);
  if (stage2Minutes != null && stage2Minutes > Number(config.coolStage2MaxMinutes)) {
    issues.push(t(
      `Cooling Stage 2: took ${stage2Minutes} min, exceeding the ${config.coolStage2MaxMinutes} min limit.`,
      `Enfriamiento Etapa 2: tomó ${stage2Minutes} min, superando el límite de ${config.coolStage2MaxMinutes} min.`,
      lang
    ));
  }
  if (run.coolStage2EndTempF != null && Number(run.coolStage2EndTempF) > Number(config.coolStage2TargetTempF)) {
    issues.push(t(
      `Cooling Stage 2: ended at ${Number(run.coolStage2EndTempF).toFixed(1)}°F, above the ${config.coolStage2TargetTempF}°F target.`,
      `Enfriamiento Etapa 2: terminó a ${Number(run.coolStage2EndTempF).toFixed(1)}°F, sobre el objetivo de ${config.coolStage2TargetTempF}°F.`,
      lang
    ));
  }

  return issues;
}

function RunVerify({ runId }) {
  const { lang } = useLanguage();
  const [run, setRun] = useState(null);
  const [config, setConfig] = useState(null);
  const [ingredientLots, setIngredientLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const [deviationFlag, setDeviationFlag] = useState(false);
  const [deviationDefaulted, setDeviationDefaulted] = useState(false);
  const [deviationNotes, setDeviationNotes] = useState('');
  const [correctiveActionNotes, setCorrectiveActionNotes] = useState('');
  const [visualInspections, setVisualInspections] = useState([]);

  const load = useCallback(async () => {
    try {
      const data = await prepCcpService.getRun(runId);
      setRun(data.run);
      setConfig(data.config || null);
      setIngredientLots(data.ingredientLots || []);
      if (data.run?.batchId && data.run?.productId) {
        sqfService.getChecklistLogsForBatch(data.run.batchId, data.run.productId).then(setVisualInspections).catch(() => setVisualInspections([]));
      }
      if (data.run?.deviation) {
        setDeviationFlag(true);
        setDeviationNotes(data.run.deviationNotes || '');
        setCorrectiveActionNotes(data.run.correctiveActionNotes || '');
        setDeviationDefaulted(true);
      }
    } catch {
      setError(t('Failed to load run.', 'Error al cargar la corrida.', lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  const autoDeviations = run && config ? detectDeviations(run, config, lang) : [];

  // Default the checkbox on once we know whether anything was actually detected — but only once,
  // so QA can still freely uncheck/recheck it afterward without being fought by this effect.
  useEffect(() => {
    if (!deviationDefaulted && run && config) {
      if (autoDeviations.length > 0) setDeviationFlag(true);
      setDeviationDefaulted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, config, deviationDefaulted]);

  const handleVerify = async () => {
    setError('');
    if (deviationFlag && !correctiveActionNotes.trim()) {
      setError(t('Corrective action notes are required when recording a deviation.', 'Las notas de acción correctiva son obligatorias al registrar una desviación.', lang));
      return;
    }
    setVerifying(true);
    try {
      await prepCcpService.verifyAndLog(runId, {
        deviation: deviationFlag,
        deviationNotes: deviationFlag ? deviationNotes : '',
        correctiveActionNotes: deviationFlag ? correctiveActionNotes : '',
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to verify.', 'Error al verificar.', lang));
    } finally {
      setVerifying(false);
    }
  };

  if (loading) return <div className="container py-5 text-center"><span className="spinner-border" /></div>;
  if (!run) return <div className="container py-5">{error}</div>;

  const missingLotCodes = ingredientLots.filter(l => !l.ingredientLotCode);
  const yieldLossPct = run.rawInputWeightKg && run.cookedOutputWeightKg
    ? ((Number(run.rawInputWeightKg) - Number(run.cookedOutputWeightKg)) / Number(run.rawInputWeightKg)) * 100
    : null;

  return (
    <div className="container py-4" style={{ maxWidth: 720 }}>
      <Link to="/ccp/prep/verify" className="text-muted text-decoration-none d-block mb-2" style={{ fontSize: 13 }}>← {t('Queue', 'Cola', lang)}</Link>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <h4 className="fw-bold mb-0">{run.Product?.name} — {run.Batch?.lotCode}</h4>
      </div>

      <VisualInspectionsList inspections={visualInspections} />

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <h6 className="fw-bold mb-2">{t('Summary', 'Resumen', lang)}</h6>
          <table className="table table-sm mb-0">
            <tbody>
              <tr><td>{t('Cook final temp', 'Temp. final de cocción', lang)}</td><td>{run.cookFinalTempF}°F</td></tr>
              <tr><td>{t('Cool Stage 1 end temp', 'Temp. final Etapa 1', lang)}</td><td>{run.coolStage1EndTempF}°F</td></tr>
              <tr><td>{t('Cool Stage 2 end temp', 'Temp. final Etapa 2', lang)}</td><td>{run.coolStage2EndTempF}°F</td></tr>
              <tr><td>{t('Raw input weight', 'Peso de entrada crudo', lang)}</td><td>{run.rawInputWeightKg != null ? `${Number(run.rawInputWeightKg).toFixed(3)} kg` : '—'}</td></tr>
              <tr><td>{t('Cooked output weight', 'Peso de salida cocido', lang)}</td><td>{run.cookedOutputWeightKg != null ? `${Number(run.cookedOutputWeightKg).toFixed(3)} kg` : '—'}</td></tr>
              <tr><td>{t('Yield loss', 'Pérdida de rendimiento', lang)}</td><td>{yieldLossPct != null ? `${yieldLossPct.toFixed(1)}%` : '—'}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <h6 className="fw-bold mb-2">{t('Ingredients', 'Ingredientes', lang)}</h6>
          <table className="table table-sm mb-0">
            <thead><tr><th>{t('Ingredient', 'Ingrediente', lang)}</th><th>{t('Lot Code', 'Código de Lote', lang)}</th><th className="text-end">kg</th></tr></thead>
            <tbody>
              {ingredientLots.map(l => (
                <tr key={l.id} className={!l.ingredientLotCode ? 'table-danger' : ''}>
                  <td>{l.Ingredient?.name}</td><td>{l.ingredientLotCode || t('missing', 'faltante', lang)}</td>
                  <td className="text-end">{l.quantityKg != null ? Number(l.quantityKg).toFixed(3) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {run.status === 'VERIFIED' ? (
        <>
          <div className="alert alert-secondary">🔒 {t('Already verified and locked.', 'Ya verificado y bloqueado.', lang)}</div>
          {run.deviation && (
            <div className="card shadow-sm mb-3 border-warning">
              <div className="card-body">
                <h6 className="fw-bold text-warning mb-2">⚠ {t('Deviation Recorded', 'Desviación Registrada', lang)}</h6>
                {run.deviationNotes && (
                  <p className="mb-2"><strong>{t('Notes:', 'Notas:', lang)}</strong> {run.deviationNotes}</p>
                )}
                <p className="mb-0"><strong>{t('Corrective Action:', 'Acción Correctiva:', lang)}</strong> {run.correctiveActionNotes || '—'}</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {missingLotCodes.length > 0 && (
            <div className="alert alert-warning py-2">
              ⚠ {t('Some ingredients are missing a lot code — review before verifying.', 'Algunos ingredientes no tienen código de lote — revisa antes de verificar.', lang)}
            </div>
          )}

          <div className="card shadow-sm mb-3 border-warning">
            <div className="card-body">
              <h6 className="fw-bold mb-2">⚠ {t('Critical Limit Check', 'Verificación de Límites Críticos', lang)}</h6>
              {autoDeviations.length === 0 ? (
                <div className="text-success small mb-0">
                  ✓ {t('No deviations detected against the configured critical limits.', 'No se detectaron desviaciones contra los límites críticos configurados.', lang)}
                </div>
              ) : (
                <ul className="text-danger small mb-0 ps-3">
                  {autoDeviations.map((issue, i) => <li key={i}>{issue}</li>)}
                </ul>
              )}

              <div className="form-check mt-3">
                <input type="checkbox" className="form-check-input" id="deviationFlag"
                  checked={deviationFlag} onChange={e => setDeviationFlag(e.target.checked)} />
                <label className="form-check-label" htmlFor="deviationFlag">
                  {t('Record this batch as having a deviation requiring corrective action', 'Registrar este lote como que tiene una desviación que requiere acción correctiva', lang)}
                </label>
              </div>

              {deviationFlag && (
                <div className="mt-2">
                  <label className="form-label small mb-1">{t('Deviation notes', 'Notas de desviación', lang)}</label>
                  <textarea className="form-control form-control-sm mb-2" rows={2}
                    value={deviationNotes} onChange={e => setDeviationNotes(e.target.value)}
                    placeholder={t('What happened and how it was found', 'Qué sucedió y cómo se detectó', lang)} />
                  <label className="form-label small mb-1">
                    {t('Corrective action', 'Acción correctiva', lang)} <span className="text-danger">*</span>
                  </label>
                  <textarea className="form-control form-control-sm" rows={2}
                    value={correctiveActionNotes} onChange={e => setCorrectiveActionNotes(e.target.value)}
                    placeholder={t('What was done about it (e.g. product held/discarded, re-cooked, disposition decision)', 'Qué se hizo al respecto (ej. producto retenido/descartado, re-cocinado, decisión de disposición)', lang)} />
                </div>
              )}
            </div>
          </div>

          <div className="alert alert-info py-2" style={{ fontSize: 13 }}>
            {t('Verifying will lock this run and automatically credit', 'Verificar bloqueará esta corrida y acreditará automáticamente', lang)}{' '}
            <strong>{run.cookedOutputWeightKg != null ? Number(run.cookedOutputWeightKg).toFixed(3) : '?'} kg</strong>{' '}
            {t('into Ingredient Inventory as internally-produced stock, at its real cost.', 'al Inventario de Ingredientes como existencias producidas internamente, a su costo real.', lang)}
          </div>
          <button className="btn btn-success btn-lg" disabled={verifying} onClick={handleVerify}>
            {verifying ? t('Verifying…', 'Verificando…', lang) : `✅ ${t('Verify & Lock', 'Verificar y Bloquear', lang)}`}
          </button>
        </>
      )}

    </div>
  );
}

export default function PrepCcpVerifyPage() {
  const { runId } = useParams();
  return runId ? <RunVerify runId={runId} /> : <QueueList />;
}
